import '../App.css'
import envelopeOptions from '../images/envelope-options.png'
import assemblyExploded from '../images/assembly-exploded.png'
import members from '../images/members.png'
import layers from '../images/layers.png'
import scenarios from '../images/scenarios.png'
import { Divider, Grid, Paper, Stack, Typography } from "@mui/material"
import UIButtonLoadProject from "./UIButtonLoadProject"
import UIButtonNewProject from "./UIButtonNewProject"
import { welcomeButtonStyle } from '../Graphics'

type Props = {
  updateApp: Function
}

export default function UIWelcome({updateApp}: Props){
  const paperStyle = {margin: 3, padding: 3, textAlign: 'left', border: 1, borderColor: 'lightgray', borderRadius: 3}

  return(
    <div>
      <Grid container sx={{ mt: 13 }} justifyContent={'center'} >
        <Grid item xs={10} sm={8} md={7} lg={6} xl={5}>
          <Paper sx={paperStyle}>
            <Typography variant='h3' color={'primary'}>
              Welcome to Feather</Typography>
            <Typography variant='h5' py={2} justifyContent={'center'}>
            Feather is a web-based, baseline agnostic embodied carbon calculator 
            that gives designers real-time comparative analysis between different 
            building envelope assemblies and structural system designs.</Typography>
          </Paper>
          <Paper sx={paperStyle}>
            <Typography variant='h3' color={'primary'} paddingBottom={1}>
              Get Started</Typography>
            <Typography sx={{opacity:0.5}}>
              For best results, please use a desktop browser.</Typography>
            <Stack direction={'column'}>
              <UIButtonLoadProject updateApp={updateApp}
                  style={welcomeButtonStyle}
                  text='Load an existing project'
                  variant='contained'
                  color='primary' />
              <UIButtonNewProject updateApp={updateApp}
                text='Start new project'
                tooltip='Start a new empty project'
                variant='contained'/>
              <UIButtonNewProject updateApp={updateApp} example
                text='open example project'
                tooltip='Open a project populated with example assemblies'
                variant='contained'/>
            </Stack>
            
          </Paper>
          <Paper sx={paperStyle}>
            <Typography variant='h4' color={'primary'}>Purpose</Typography>
            <Typography component="span">
              <p>Feather is designed to be fast and intuitive.</p>
              <p>By enabling rapid design iteration, 
                Feather gives designers a critical piece of information to supplement 
                other important early design consideration like aesthetics, cost, and other 
                environmental concerns.</p>
              <div className={'centered-image'}>
                <img src={envelopeOptions} width={'70%'}/>
              </div>
              <p>Requiring only envelope assemblies and area quantities, Feather's simple interface 
                reduces common barriers to understanding embodied carbon impacts of 
                early design decisions, such as time investment or specialized software knowledge. 
                </p>
              <div className={'centered-image'}>
                <img src={assemblyExploded} width={'70%'}/>
              </div>
              <p>This speed and simplicity does not come at the expense of specificity. 
                Wall assemblies are fully customizable, giving designers and engineers the 
                opportunity to represent project-specific envelope conditions.</p>
              
            </Typography>
          </Paper>
          <Paper sx={paperStyle}>
            <Typography variant='h4' color={'primary'}>Feather is...</Typography>
            <Typography component="span">
              <p><b>Web-based!</b></p>
              <li>Rather than relying on specialized software, Feather runs on your web browser 
                of choice. You can save your file to your local computer to pick up later or share
                with others. Leave the detailed 3D modeling for later in the project.</li>
              <li>Feather has no user login and no data collection of any kind.</li>
              <p><b>Fast!</b></p>
              <li>Analysis results update in real time. Additionally, the built-in assembly library 
                gives users a great many options to start an analysis, and new assemblies can be made 
                in minutes (or less).</li>
              <p><b>Baseline agnostic!</b></p>
              <li>You're designing a building that will ultimately be made of stuff. While this 
                may not be the most environmentally friendly choice, now you're doing the best you can! 
                Feather does not compare your design against other buildings, but rather allows 
                you to set your own baseline based on any external conditions you need to contend with.</li>
              <p><b>Bespoke!</b></p>
              <li>A wide selection of common materials, member shapes, and assemblies will be available in Feather, 
                but all of these can be customized to best represent your designs.</li>
              <p><b>Rigorous!</b></p>
              <li>Feather uses the current <a href="https://carbonleadershipforum.org/2025-clf-north-american-material-baselines/" target={'_blank'} rel={'noreferrer'}>
              CLF North American Material Baselines Report</a> for the vast majority of its standard embodied carbon values. In cases where the CLF Report did not provide sufficient data, Feather relies on values from EC3 (Building Transparency), Industry Average EPDs, Tally, or, as a last resort, product specific EPDs in a few cases. For all non-standard cases, we have noted where we derive the embodied carbon data.</li>
              <li>In alignment with current industry best practices, Feather does not include "negative emissions" from biogenic carbon as part of its baseline assumptions about GWP values.</li>
              <li>Feather went through extensive testing against known embodied carbon calculators like 
                Tally and EC3. Feather results matched almost exactly with EC3 and differed slightly from Tally. 
                This was in line with our expectation because EC3 baselines and Feather use the same data, 
                while Tally uses a different underlying dataset.</li>
              <p><b>Evolving!</b></p>
              <li>If there is something you think could work better or a feature you think we could add, 
                please let us know! <a href='mailto:feather@wrnsstudio.com'>feather@wrnsstudio.com</a> </li>
            </Typography>
          </Paper>
          <Paper sx={paperStyle}>
            <Typography variant='h4' color={'primary'}>Feather is not...</Typography>
            <Typography component="span">
              <p><b>An LCA tool!</b></p>
              <li>Feather is an early design tool to help designers understand the 
                comparative embodied carbon impacts of envelope design decisions.</li>
              <li>Feather's analysis includes life cycle stages A1-A3, and looks only 
                at embodied carbon analysis. Full LCA analysis includes many other life 
                cycle stages as well as other associated environmental impacts.</li>
              <p><b>Perfect!</b></p>
              <li>Our highest hope is that you use this tool to make more informed decisions. 
                Feather is based on industry average data, so it may not reflect your design 
                scenario down to the last screw or clip. With that said,</li>
              <li>We're only human and Feather may have bugs. If you find one, 
                please let us know. <a href='mailto:feather@wrnsstudio.com'>feather@wrnsstudio.com</a> </li>
            </Typography>
          </Paper>
          <Paper sx={paperStyle}>
            <Typography variant='h4' color={'primary'}>How it works</Typography>
            <Typography paddingBottom={6} component="span">
              <div className={'centered-image'}>
                <img src={members} width={'50%'}/>
              </div>
              <p><b>1. Define Materials and Members</b></p>
              <p>Identify the framing member profiles and materials to be used in the assemblies. For example, 
                wood or metal studs, wide-flange beams, or aluminum curtain wall mullions. 
                Material data from CLF Baselines can be used by default, or customized to match your
                product specific EPDs.</p>
              <Divider/>
            </Typography>
            <Typography paddingBottom={6} component="span">
              <div className={'centered-image'}>
                <img src={layers} width={'70%'}/>
              </div>
              <p><b>2. Define Layers of an Assembly</b></p>
              <p>An assembly or wall type is composed of solid and framed layers, by defining the thickness 
                of material or the member spacing.</p>
              <Divider/>
            </Typography>
            <Typography paddingBottom={6} component="span">
              <div className={'centered-image'}>
                <img src={scenarios} width={'70%'}/>
              </div>
              <p><b>3. Visualize the Impacts and Compare Scenarios</b></p>
              <p>Materials in the assembly are color-coded based on relative carbon intensity, 
                helping identify where material reductions will be most effective. Create and compare different 
                scenarios for a building envelope using different quantities of each assembly.
              </p>
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      <div id={'background'}></div>
    </div>
  )
}